/**
 * Audit-log viewer (tier offer: Pro = 90-day history, Enterprise =
 * unlimited + CSV export). Admin-only server-side (tenant.manage).
 */
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

export interface AuditLogEntry {
  id: string;
  action: string;
  actor_id: string | null;
  actor_name: string | null;
  resource_type: string | null;
  resource_id: string | null;
  details: unknown;
  ip_address: string | null;
  created_at: string | null;
}

export interface AuditLogsPage {
  logs: AuditLogEntry[];
  /** 90 on Pro, null (= unlimited) on Enterprise. */
  window_days: number | null;
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface AuditLogFilters {
  page?: number;
  per_page?: number;
  action?: string;
}

export const fetchAuditLogs = async (filters: AuditLogFilters): Promise<AuditLogsPage> => {
  const response = await apiClient.get<{ data: AuditLogsPage }>('/audit-logs', {
    params: {
      page: filters.page ?? 1,
      per_page: filters.per_page ?? 25,
      ...(filters.action ? { action: filters.action } : {}),
    },
  });
  return response.data;
};

export const useAuditLogs = (
  filters: AuditLogFilters,
  enabled = true
): UseQueryResult<AuditLogsPage, Error> =>
  useQuery({
    queryKey: ['audit-logs', filters],
    queryFn: () => fetchAuditLogs(filters),
    enabled,
    staleTime: 30 * 1000,
  });

/** Enterprise-only CSV download; triggers a browser save. */
export const downloadAuditLogExport = async (): Promise<void> => {
  const response = await apiClient.get<Blob>('/audit-logs/export', {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response as unknown as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tereiko-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
